import ko from "knockout";
import _ from "lodash";
import AnnotationTypeResources from "enums/AnnotationTypeResources";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import TranscriptUIAnnotationTypeResources from "enums/TranscriptUIAnnotationTypeResources";
import ViewModel from "viewModels/ViewModel";
import $ from "jquery";

const AnnotationView = ViewModel.extend(function (
    annotation,
    totalAudioDurationInMs,
    parentWidth,
    annotationClickLocation,
    isSeeking
) {
    const self = this;

    const halfIconWidth = 21 / 2;
    const absoluteDurationMs = ko.utils.unwrapObservable(
        annotation.absoluteDurationMs
    );
    const location = ko.utils.unwrapObservable(annotation.recordingLocation);
    const type = ko.utils.unwrapObservable(annotation.type);
    const durationMs =
        type && type.toLowerCase() !== "bookmark"
            ? ko.utils.unwrapObservable(annotation.recordingDurationMs)
            : null;
    const description = ko.utils.unwrapObservable(annotation.description);
    const phrase = ko.utils.unwrapObservable(annotation.phrase);
    self.getLocation = function () {
        return location;
    };

    self.getType = function () {
        return type;
    };

    self.animationName = ko.observable("none");
    self.topPosition = ko.observable("auto");
    self.leftPosition = ko.observable("auto");

    // Find where this annotation is on the time axis
    self.percentPosition = ko.observable(0);
    if (totalAudioDurationInMs > 0) {
        const percentPosition = (location / totalAudioDurationInMs) * 100;
        if (!isNaN(percentPosition)) {
            self.percentPosition(percentPosition);
        }
    }

    self.displayPercentPosition = self.computed(function () {
        if (!parentWidth) {
            return self.percentPosition();
        }
        if ((self.percentPosition() / 100) * parentWidth < halfIconWidth) {
            return (halfIconWidth / parentWidth) * 100;
        } else if (
            (self.percentPosition() / 100) * parentWidth >
            parentWidth - halfIconWidth
        ) {
            return ((parentWidth - halfIconWidth) / parentWidth) * 100;
        } else {
            return self.percentPosition();
        }
    });

    // Determine if this annotation has a duration and if so, what it is
    self.hasDuration = ko.observable(false);
    self.durationPercentage = ko.observable(null);
    self.totalAudioDurationInMs = totalAudioDurationInMs;
    if (totalAudioDurationInMs && totalAudioDurationInMs > 0) {
        if (annotation && _.isInteger(durationMs)) {
            self.hasDuration(true);
            self.durationPercentage(
                (durationMs / totalAudioDurationInMs) * 100
            );
        }
    }

    // What kind of annotation is it?
    const annotationTypeResource =
        AnnotationTypeResources[type.toLowerCase()] ||
        TranscriptUIAnnotationTypeResources[type.toLowerCase()] ||
        AnnotationTypeResources.unknown;
    self.iconClassName = ko.observable(annotationTypeResource.iconClassName);
    self.labelKey = ko.observable(annotationTypeResource.labelKey);
    self.iconName = ko.observable(annotationTypeResource.iconName);
    self.className = ko.observable(annotationTypeResource.className);
    const updatedLabels = {
        positivesentiment: "annotation.positive",
        negativesentiment: "annotation.negative",
    };
    if (type === "positivesentiment" || type === "negativesentiment") {
        self.labelKey(updatedLabels[type]);
    }
    self.topicNumber = ko.observable(annotation.topicNumber);
    const topicInteger = parseInt(annotation.topicNumber);
    const iconFontSize = isNaN(topicInteger)
        ? "inherit"
        : topicInteger > 99
        ? "9px"
        : "12px";
    self.iconFontSize = ko.observable(iconFontSize);

    self.sentimentScores = Array([]);
    self.empathyScores = Array([]);

    // Fill out tooltip details
    self.isTooltipVisible = ko.observable(false);
    self.displayProperties = ko.observableArray([]);
    let timeKey = "annotation.time";
    let durationKey = "annotation.duration";
    const iconKey = "annotation.icon";
    let realTimeDurationKey = "";
    let reasonKey = "";

    if (type === "Pause") {
        timeKey = "annotation.timePause";
        durationKey = "annotation.durationPause";
        realTimeDurationKey = "annotation.realTimeDuration";
        reasonKey = "annotation.reason";
    }

    const topicNumberValue =
        type === "topic"
            ? ko.utils.unwrapObservable(annotation.topicNumber) + ""
            : "";
    self.displayProperties.push({
        nameKey: iconKey,
        value: "",
        iconClassName: ko.observable(
            TranscriptUIAnnotationTypeResources[type.toLowerCase()]
                ? annotationTypeResource.iconClassName
                : null
        ),
        iconName: ko.observable(
            TranscriptUIAnnotationTypeResources[type.toLowerCase()]
                ? annotationTypeResource.iconName
                : null
        ),
        className: ko.observable(
            TranscriptUIAnnotationTypeResources[type.toLowerCase()]
                ? annotationTypeResource.className
                : null
        ),
        topicNumber: topicNumberValue,
        iconFontSize: ko.utils.unwrapObservable(self.iconFontSize),
    });

    const reduceRedactedPhraseLength = function (phraseWords) {
        if (!phraseWords || !phraseWords.length) {
            return [];
        }
        let length = 0;
        phraseWords = phraseWords.filter(
            (word) => (length += word.length) <= 50
        );
        if (length >= 50) {
            phraseWords.push("...");
        }
        return phraseWords;
    };

    const removeIrrelevantWordIndices = function (
        recognizedNamedEntitiesIntervals,
        phraseWords
    ) {
        if (!phraseWords) {
            return;
        }
        let maxIndex = -1;
        maxIndex =
            phraseWords.length === 0
                ? maxIndex
                : phraseWords[phraseWords.length - 1] === "..."
                ? phraseWords.length - 2
                : phraseWords.length - 1;
        return maxIndex === -1 ||
            !recognizedNamedEntitiesIntervals ||
            !recognizedNamedEntitiesIntervals.length
            ? []
            : recognizedNamedEntitiesIntervals
                  .map((interval) =>
                      interval[1] <= maxIndex
                          ? interval
                          : [interval[0], maxIndex]
                  )
                  .filter((interval) => interval[0] <= maxIndex);
    };

    const buildPhraseWords = function (
        phraseWords,
        recognizedNamedEntitiesIntervals
    ) {
        phraseWords = reduceRedactedPhraseLength(phraseWords);
        recognizedNamedEntitiesIntervals = removeIrrelevantWordIndices(
            recognizedNamedEntitiesIntervals,
            phraseWords
        );
        const isRedacted = !!(
            recognizedNamedEntitiesIntervals &&
            recognizedNamedEntitiesIntervals.length
        );
        return [phraseWords, recognizedNamedEntitiesIntervals, isRedacted];
    };

    // Track if the annotation is displaying an icon in its display properties
    self.annotationHasIconDisplay = ko.observable(
        !!TranscriptUIAnnotationTypeResources[type.toLowerCase()]
    );
    const roundDownPrecision = 1;

    const annotationTypes = [
        "Pause",
        "Bookmark",
        "Hold",
        "positivesentiment",
        "negativesentiment",
        "agentempathy",
        "unhelpfulbehavior",
    ];

    if (annotationTypes.includes(type)) {
        self.displayProperties.push({
            nameKey: "annotation.type",
            value: ko.i18n.renderString("annotation.type." + type),
        });
    }

    self.displayProperties.push({
        nameKey: timeKey,
        value: ko.i18n.renderString("annotation.secondsValue", {
            seconds:
                Math.floor((location / 1000) * roundDownPrecision) /
                roundDownPrecision,
        }),
    });
    if (self.hasDuration()) {
        self.displayProperties.push({
            nameKey: "annotation.duration",
            value: ko.i18n.renderString("annotation.secondsValue", {
                seconds: (durationMs / 1000.0).toFixed(0),
            }),
        });
    }
    if (realTimeDurationKey) {
        //Only for Secure Pauses
        self.displayProperties.push({
            nameKey: realTimeDurationKey,
            value: ko.i18n.renderString("annotation.secondsValue", {
                seconds: (absoluteDurationMs / 1000).toFixed(0),
            }),
        });
    }
    if (reasonKey && annotation.reason !== undefined) {
        //Only for Pause events
        self.displayProperties.push({
            nameKey: reasonKey,
            value: ko.i18n.renderString(
                "annotation.reason." + annotation.reason
            ),
        });
    }
    if (type === "positivesentiment" || type === "negativesentiment") {
        const score = Math.round(
            ko.utils.unwrapObservable(annotation.sentiment) * 100
        );
        self.sentimentScores.push({
            nameKey: "annotation.score",
            value: (score >= 0 ? "+" : "") + score,
        });
    }
    if (type === "agentempathy" || type === "unhelpfulbehavior") {
        const score = Math.round(
            ko.utils.unwrapObservable(annotation.empathy) * 100
        );
        self.empathyScores.push({
            nameKey: "annotation.score",
            value: (score >= 0 ? "+" : "") + score,
        });
    }
    if (annotation.topic) {
        self.displayProperties.push({
            nameKey: "annotation.topicName",
            value: ko.utils.unwrapObservable(annotation.topic),
        });
    }
    if (annotation.description) {
        self.displayProperties.push({
            nameKey: "annotation.description",
            value: description,
        });
    }
    if (annotation.phrase) {
        const [
            phraseWords,
            recognizedNamedEntitiesIntervals,
            isRedactedPhrase,
        ] = buildPhraseWords(
            ko.utils.unwrapObservable(annotation.phraseWords),
            ko.utils.unwrapObservable(annotation.recognizedNamedEntitiesIndices)
        );

        // Some languages do not require a space between words,
        // Default to true (using space) if not specified
        const needsWhitespace =
            ko.utils.unwrapObservable(annotation.needsWhitespace) !== false;

        const value = isRedactedPhrase
            ? phraseWords
            : phrase.length < 50
            ? phrase
            : phrase.substring(0, 50) + "...";

        self.displayProperties.push({
            nameKey: annotation.topic
                ? "annotation.transcriptPhrase"
                : "annotation.phrase",
            value: value,
            isRedactedPhrase: isRedactedPhrase,
            needsWhitespace: needsWhitespace,
            isRedactedInterval: (index, isIntervalBegin) =>
                recognizedNamedEntitiesIntervals
                    .map((interval) =>
                        isIntervalBegin ? interval[0] : interval[1]
                    )
                    .includes(ko.utils.unwrapObservable(index)),
        });
    }

    if (annotation.animatePlacement) {
        self.animationName("drop-in-place");
        annotation.animatePlacement = false;
    }

    self.removeAnimation = function (_, event) {
        // an 0 elapsedTime event can be triggered before the actual animation plays
        if (event.elapsedTime > 0 && type.toLowerCase() === "bookmark") {
            self.animationName(null);
        }
    };

    self.isTranscriptUIAnnotationType = function () {
        return !!TranscriptUIAnnotationTypeResources[type.toLowerCase()];
    };

    self.showTooltip = function () {
        if (self.isTooltipVisible()) {
            return;
        }
        self.isTooltipVisible(true);
        const leftOffset =
            $(".audio-wave-container").offset().left +
            (self.displayPercentPosition() / 100) *
                $(".audio-wave-container").width();
        const rightOffset =
            $(".playback-waveform-overlay").offset().left +
            (self.displayPercentPosition() / 100) *
                $(".playback-waveform-overlay").width();
        self.leftPosition(rightOffset - leftOffset + "px");

        const playbackIcon = $(".waveform-eventBar .playback-event-icon")[0];
        if (playbackIcon === undefined) return; // No need to set y position for old player (playbackIcon does not exist in old player)
        const top =
            playbackIcon.getBoundingClientRect().y +
            playbackIcon.getBoundingClientRect().height / 2;
        const topOffset = $(".audio-wave-container")[0].getBoundingClientRect()
            .top;
        //Set popover y position onto the waveform
        self.topPosition(top - topOffset + 10 + "px");

        document.addEventListener("keydown", handleKeyDownEvent);
    };

    const shouldSkipTooltipUpdate = function (event) {
        const targetIndex = event?.target?.closest(
            ".playback-event-annotation-wrapper"
        )?.dataset?.annotationIndex;
        const relatedTargetIndex = event?.relatedTarget?.closest(
            ".playback-event-annotation-wrapper"
        )?.dataset?.annotationIndex;
        if (
            targetIndex &&
            relatedTargetIndex &&
            targetIndex === relatedTargetIndex
        ) {
            return true;
        }
        return false;
    };

    self.showTooltipMouseOver = function (data, event) {
        // Do not display tooltips when seeking or when mouse is moved on to icon from the popover content
        if (
            isSeeking() ||
            shouldSkipTooltipUpdate(event) ||
            self.isTooltipVisible()
        ) {
            return;
        }
        self.isTooltipVisible(true);
        const target = event.target.children[0];
        const targetCenterX =
            target.getBoundingClientRect().x +
            target.getBoundingClientRect().width / 2;
        const targetCenterY =
            target.getBoundingClientRect().y +
            target.getBoundingClientRect().height / 2;
        const left = targetCenterX;
        const top = targetCenterY;
        const leftOffset =
            (self.displayPercentPosition() / 100) *
                $(".audio-wave-container").width() +
            $(".audio-wave-container").offset().left;
        const topOffset = $(".audio-wave-container")[0].getBoundingClientRect()
            .top;
        self.leftPosition(left - leftOffset + "px");
        // popover sentiment needs to be placed away from the cursor to avoid firing mouseleave event on the icon
        self.topPosition(top - topOffset + 10 + "px");
        document.addEventListener("keydown", handleKeyDownEvent);
    };

    const handleKeyDownEvent = function (event) {
        if (!self.isTooltipVisible()) return;

        if (event.key === "Escape") {
            self.hideTooltip(null, event);
            return;
        }

        const targetIndex = event?.target?.closest(
            ".playback-event-annotation-wrapper"
        )?.dataset?.annotationIndex;

        const targetPopoverContent = document.getElementById(
            `annotation-popover-content-${targetIndex}`
        );

        if (!targetPopoverContent) return;

        const offsetHeight = targetPopoverContent.offsetHeight;
        const scrollHeight = targetPopoverContent.scrollHeight;
        const scrollTop = targetPopoverContent.scrollTop;

        let newScrollTop = 0;

        switch (event.key) {
            case "ArrowDown": {
                if (scrollTop + offsetHeight < scrollHeight) {
                    newScrollTop = Math.min(100, scrollHeight - offsetHeight);
                }
                break;
            }
            case "ArrowUp": {
                if (targetPopoverContent.scrollTop > 0) {
                    event.preventDefault();
                    newScrollTop = Math.max(-100, -scrollTop);
                }
                break;
            }
            case "PageDown": {
                if (scrollTop + offsetHeight < scrollHeight) {
                    newScrollTop = Math.min(
                        offsetHeight * 0.95,
                        scrollHeight - offsetHeight
                    );
                }
                break;
            }
            case "PageUp": {
                newScrollTop = Math.max(-(offsetHeight * 0.95), -scrollTop);
                break;
            }
            default:
                return;
        }

        event.preventDefault();
        targetPopoverContent.scrollBy({
            behavior: "smooth",
            top: newScrollTop,
        });
    };

    self.hideTooltip = function (data, event) {
        if (shouldSkipTooltipUpdate(event)) {
            return;
        }
        self.isTooltipVisible(false);
        document.removeEventListener("keydown", handleKeyDownEvent);
    };

    self.annotationClick = function () {
        const transcriptUIAnnotationTypeResource =
            TranscriptUIAnnotationTypeResources[type.toLowerCase()] ||
            undefined;
        if (transcriptUIAnnotationTypeResource && location) {
            annotationClickLocation(location);
        }
    };

    //Add the information from an additional annotation at the same location to this one.
    //Eventually we may want to do something more sophisticated than this but for now
    //this only adds an additional description
    self.addAnnotation = function (annotation) {
        const unwrappedAnnotation = ko.toJS(annotation);
        // If the new annotation has a recording duration set, figure out whether it's greater than
        // the current annotation's
        // Set this AnnotationView's durationPercentage to whichever is higher
        if (
            unwrappedAnnotation &&
            _.isInteger(unwrappedAnnotation.recordingDurationMs) &&
            self.totalAudioDurationInMs &&
            self.totalAudioDurationInMs > 0
        ) {
            const newAnnotationDurationPercentage =
                (unwrappedAnnotation.recordingDurationMs /
                    self.totalAudioDurationInMs) *
                100;
            if (self.hasDuration()) {
                if (
                    newAnnotationDurationPercentage > self.durationPercentage()
                ) {
                    self.durationPercentage(newAnnotationDurationPercentage);
                }
            } else {
                self.hasDuration(true);
                self.durationPercentage(newAnnotationDurationPercentage);
            }
        }

        // If the display properties doesn't include the first annotation icon, include it now
        if (!self.annotationHasIconDisplay() && self.displayProperties()) {
            const firstAnnotationTypeResource =
                AnnotationTypeResources[self.getType().toLowerCase()];
            self.displayProperties()[0].iconClassName(
                firstAnnotationTypeResource
                    ? firstAnnotationTypeResource.iconClassName
                    : null
            );

            self.displayProperties()[0].iconName(
                firstAnnotationTypeResource
                    ? firstAnnotationTypeResource.iconName
                    : null
            );
            self.displayProperties()[0].className(
                firstAnnotationTypeResource
                    ? firstAnnotationTypeResource.className
                    : null
            );
            self.annotationHasIconDisplay(true);
        }

        if (unwrappedAnnotation.animatePlacement) {
            self.animationName("drop-in-place");
            unwrappedAnnotation.animatePlacement = false;
        }
        const annotationTypeResource =
            AnnotationTypeResources[unwrappedAnnotation.type.toLowerCase()] ||
            AnnotationTypeResources.unknown;

        let timeKey = "annotation.time";
        let realTimeDurationKey = "";
        let reasonKey = "";

        if (unwrappedAnnotation.type === "Pause") {
            timeKey = "annotation.timePause";
            durationKey = "annotation.durationPause";
            realTimeDurationKey = "annotation.realTimeDuration";
            reasonKey = "annotation.reason";
        }

        self.displayProperties.push({
            nameKey: "annotation.icon",
            value: "",
            iconClassName: ko.observable(
                self.annotationHasIconDisplay()
                    ? annotationTypeResource.iconClassName
                    : null
            ),
            iconName: ko.observable(
                self.annotationHasIconDisplay()
                    ? annotationTypeResource.iconName
                    : null
            ),
            className: ko.observable(
                self.annotationHasIconDisplay()
                    ? annotationTypeResource.className
                    : null
            ),
            topicNumber: "",
            iconFontSize: "inherit",
        });

        const type = unwrappedAnnotation
            ? ko.utils.unwrapObservable(unwrappedAnnotation.type)
            : null;

        if (type === "Pause" || type === "Bookmark" || type === "Hold") {
            self.displayProperties.push({
                nameKey: "annotation.type",
                value: ko.i18n.renderString("annotation.type." + type),
            });
        }

        self.displayProperties.push({
            nameKey: timeKey,
            value: ko.i18n.renderString("annotation.secondsValue", {
                seconds:
                    Math.floor(
                        (unwrappedAnnotation.recordingLocation / 1000) *
                            roundDownPrecision
                    ) / roundDownPrecision,
            }),
        });

        if (unwrappedAnnotation.description) {
            self.displayProperties.push({
                nameKey: "annotation.description",
                value: unwrappedAnnotation.description,
            });
        }

        if (
            self.hasDuration() &&
            unwrappedAnnotation &&
            type !== "Bookmark" &&
            _.isInteger(unwrappedAnnotation.recordingDurationMs)
        ) {
            self.displayProperties.push({
                nameKey: "annotation.duration",
                value: ko.i18n.renderString("annotation.secondsValue", {
                    seconds: (
                        unwrappedAnnotation.recordingDurationMs / 1000.0
                    ).toFixed(0),
                }),
            });
        }
        if (realTimeDurationKey) {
            //Only for Secure Pauses
            self.displayProperties.push({
                nameKey: realTimeDurationKey,
                value: ko.i18n.renderString("annotation.secondsValue", {
                    seconds: (
                        unwrappedAnnotation.absoluteDurationMs / 1000
                    ).toFixed(0),
                }),
            });
        }
        if (reasonKey && annotation.reason !== undefined) {
            //Only for Pause events
            self.displayProperties.push({
                nameKey: reasonKey,
                value: ko.i18n.renderString(
                    "annotation.reason." + annotation.reason
                ),
            });
        }

        // Override event icon and labelling
        const overrideAnnotationTypeResource =
            AnnotationTypeResources["combinedannotations"];
        self.iconClassName(overrideAnnotationTypeResource.iconClassName);
        self.iconName(overrideAnnotationTypeResource.iconName);
        self.className(overrideAnnotationTypeResource.className);
        self.labelKey(overrideAnnotationTypeResource.labelKey);
        self.topicNumber(null);
        self.iconFontSize("inherit");
    };

    self.mergeTranscriptUIAnnotation = function (annotation) {
        const annotationTypeResource =
            TranscriptUIAnnotationTypeResources[
                annotation.type.toLowerCase()
            ] || AnnotationTypeResources.unknown;
        const topicNumberValue =
            annotation.type === "topic"
                ? ko.utils.unwrapObservable(annotation.topicNumber) + ""
                : "";
        const topicInteger = parseInt(topicNumberValue);
        const iconFontSize = isNaN(topicInteger)
            ? "inherit"
            : topicInteger > 99
            ? "9px"
            : "12px";

        self.displayProperties.push({
            nameKey: "annotation.icon",
            value: "",
            iconClassName: ko.observable(annotationTypeResource.iconClassName),
            iconName: ko.observable(annotationTypeResource.iconName || null),
            className: ko.observable(annotationTypeResource.className || null),
            topicNumber: topicNumberValue,
            iconFontSize: iconFontSize,
        });

        //Display type for sentiment annotations other than Topic
        if (
            annotation.type !== "topic" &&
            !!TranscriptUIAnnotationTypeResources[annotation.type.toLowerCase()]
        ) {
            self.displayProperties.push({
                nameKey: "annotation.type",
                value: ko.i18n.renderString(
                    "annotation.type." +
                        ko.utils.unwrapObservable(annotation.type)
                ),
            });
        }

        if (
            annotation.type === "positivesentiment" ||
            annotation.type === "negativesentiment"
        ) {
            const score = Math.round(
                ko.utils.unwrapObservable(annotation.sentiment) * 100
            );
            self.sentimentScores.push({
                nameKey: "annotation.score",
                value: (score >= 0 ? "+" : "") + score,
            });
        }

        if (
            annotation.type === "agentempathy" ||
            annotation.type === "unhelpfulbehavior"
        ) {
            const score = Math.round(
                ko.utils.unwrapObservable(annotation.empathy) * 100
            );
            self.empathyScores.push({
                nameKey: "annotation.score",
                value: (score >= 0 ? "+" : "") + score,
            });
        }

        self.displayProperties.push({
            nameKey: timeKey,
            value: ko.i18n.renderString("annotation.secondsValue", {
                seconds: (annotation.recordingLocation / 1000.0).toFixed(0),
            }),
        });

        if (annotation.topic) {
            self.displayProperties.push({
                nameKey: "annotation.topicName",
                value: ko.utils.unwrapObservable(annotation.topic),
            });
        }

        if (annotation.phrase) {
            const [
                phraseWords,
                recognizedNamedEntitiesIntervals,
                isRedactedPhrase,
            ] = buildPhraseWords(
                ko.utils.unwrapObservable(annotation.phraseWords),
                ko.utils.unwrapObservable(
                    annotation.recognizedNamedEntitiesIndices
                )
            );

            // Some languages do not require a space between words,
            // Default to true (using space) if not specified
            const needsWhitespace =
                ko.utils.unwrapObservable(annotation.needsWhitespace) !== false;

            const overridePhrase = ko.utils.unwrapObservable(annotation.phrase);
            const value = isRedactedPhrase
                ? phraseWords
                : overridePhrase.length < 50
                ? overridePhrase
                : overridePhrase.substring(0, 50) + "...";

            self.displayProperties.push({
                nameKey: annotation.topic
                    ? "annotation.transcriptPhrase"
                    : "annotation.phrase",
                value: value,
                isRedactedPhrase: isRedactedPhrase,
                needsWhitespace: needsWhitespace,
                isRedactedInterval: (index, isIntervalBegin) =>
                    recognizedNamedEntitiesIntervals
                        .map((interval) =>
                            isIntervalBegin ? interval[0] : interval[1]
                        )
                        .includes(ko.utils.unwrapObservable(index)),
            });
        }

        // Override event icon and labelling
        const overrideAnnotationTypeResource =
            TranscriptUIAnnotationTypeResources["combinedevents"];
        self.iconClassName(overrideAnnotationTypeResource.iconClassName);
        self.iconName(overrideAnnotationTypeResource.iconName);
        self.className(overrideAnnotationTypeResource.className);
        self.labelKey(overrideAnnotationTypeResource.labelKey);
        self.topicNumber(null);
        self.iconFontSize("inherit");

        // Override color
        // Grey is already set by default
        // red if there is a negative sentiment/ unhelpful behavior and any topics
        // green if there is a positive sentiment/ agent empathy and any topics
        // purple if topics only
        const sentiments = self.sentimentScores.filter(
            (event) => event.nameKey == "annotation.score"
        );
        const empathies = self.empathyScores.filter(
            (event) => event.nameKey == "annotation.score"
        );
        const topics = self
            .displayProperties()
            .filter((event) => event.nameKey == "annotation.topicName");
        const sentimentCount = sentiments ? sentiments.length : 0;
        const empathyCount = empathies ? empathies.length : 0;
        const topicCount = topics ? topics.length : 0;

        if (topicCount && !sentimentCount && !empathyCount) {
            // There are exclusively topics in this merged annotation
            self.iconClassName(
                TranscriptUIAnnotationTypeResources["combinedtopics"]
                    .iconClassName
            );
            self.iconName(
                TranscriptUIAnnotationTypeResources["combinedtopics"].iconName
            );
            self.className(
                TranscriptUIAnnotationTypeResources["combinedtopics"].className
            );
            self.labelKey(
                TranscriptUIAnnotationTypeResources["combinedtopics"].labelKey
            );
        } else if (sentimentCount || empathyCount) {
            // There exists at least one sentiment or empathy in this merged annotation
            if (
                !sentiments.find(
                    (sentiment) => parseInt(sentiment.value) < 0
                ) &&
                !empathies.find((empathy) => parseInt(empathy.value) < 0)
            ) {
                // Of the events in this merged annotation, there are only positive ones
                self.iconClassName(
                    TranscriptUIAnnotationTypeResources[
                        "combinedsentimentspositive"
                    ].iconClassName
                );
                self.iconName(
                    TranscriptUIAnnotationTypeResources[
                        "combinedsentimentspositive"
                    ].iconName
                );
                self.className(
                    TranscriptUIAnnotationTypeResources[
                        "combinedsentimentspositive"
                    ].className
                );
            } else if (
                !sentiments.find(
                    (sentiment) => parseInt(sentiment.value) >= 0
                ) &&
                !empathies.find((empathy) => parseInt(empathy.value) >= 0)
            ) {
                // Of the events in this merged annotation, there are only negative ones
                self.iconClassName(
                    TranscriptUIAnnotationTypeResources[
                        "combinedsentimentsnegative"
                    ].iconClassName
                );
                self.iconName(
                    TranscriptUIAnnotationTypeResources[
                        "combinedsentimentsnegative"
                    ].iconName
                );
                self.className(
                    TranscriptUIAnnotationTypeResources[
                        "combinedsentimentsnegative"
                    ].className
                );
            }
        }
    };
});

export default AnnotationView;
